// THE EXTERNAL BAN's rules (#7785 part 3). Owner, 2026-11-02: "if PRs look downright dangerous or
// bad gh issues we just close them and ban the users, we hold internal developerz.ai ban list
// shared across all members"; "first we see what the PR or issue is about"; "check their other
// PRs, fresh account, followers, repos with stars: account with nothing and just PRs something,
// it's high risk". Pure: the worker's `external-ban/` is the shell, `@developerz/db` the store.
//
// WHO IS ASKED AT ALL is the trust line's (`@developerz/github` `readRepoDeveloper`): `external`
// only. A `developer` and an `unknown` never reach anything here, and neither does a bot.
//
// THE ORDER IS THE RULE ({@link externalDangerStep}). The content is read blind, with no author on
// the prompt. `clean` ends it and the profile is never fetched. `dangerous` asks a second, independent
// read whatever the profile says, because a long record buys nothing: the xz author would have scored
// very high. Only `suspect` reads the profile, and a raised profile buys a second read.
// A profile that raises nothing leaves the item held for a person: never a pass.
//
// A CLOSE AND A BAN ARE ONE ACT, ON TWO DANGEROUS READS OF THE SAME TEXT. THE FAILURE IT NAMES: one flaky
// call on a cheap model closing a person's work and banning them, say a reporter quoting someone else's
// injection. A first `dangerous` never closes: it asks the second read at once. Two reads that disagree
// hold the item open, on an audit row, for the ordinary review or triage path.
//
// NO REPUTATION SCORE. {@link authorScrutiny} answers named signals and one bit, and no value of
// any field lowers a verdict.

import { z } from 'zod';
import { endpointWire } from '../value-objects/llm-credential';
import { type Tier, Tier as Tiers } from './account';
import { looksLikeBot } from './babysit';
import { BYOKProvider, BYOKProviderSchema } from './byok';
import {
  type ExternalBanSource,
  type ExternalDangerCategory,
  type ExternalDangerVerdict,
  ExternalItemKindSchema,
  type ExternalScrutinySignal,
  ExternalScrutinySignalSchema,
  type ExternalSharedBanSource,
} from './external-ban/vocabulary';

export * from './external-ban/vocabulary';

/** What raised scrutiny means: a second read of the content, and nothing else. */
export type ExternalScrutiny = {
  readonly level: 'none' | 'raised';
  readonly signals: readonly ExternalScrutinySignal[];
};

/** What one public GitHub profile says. A count GitHub would not answer is `null`, never 0. */
export type ExternalAuthorProfile = {
  readonly createdAt: Date;
  readonly followers: number;
  /** Stars across the repos the account owns, as far as the one page read shows. */
  readonly ownedRepoStars: number;
  readonly otherPullRequests: number | null;
  readonly mergedPullRequests: number | null;
};

/** Younger than this is a fresh account. A month outlives any "made it for this PR" account. */
export const EXTERNAL_FRESH_ACCOUNT_MS = 30 * 86_400_000;

/**
 * The profile's signals, and whether they raise scrutiny. `null` is an unavailable profile: no
 * signal, because a failed read is held against nobody. Raised takes an account with no audience
 * and nothing anyone starred, plus either youth or PRs elsewhere that never merged, the owner's
 * "account with nothing and just PRs something". An empty, quiet, old account is not raised.
 */
export function authorScrutiny(profile: ExternalAuthorProfile | null, now: Date): ExternalScrutiny {
  if (profile === null) return { level: 'none', signals: [] };
  const fresh = now.getTime() - profile.createdAt.getTime() < EXTERNAL_FRESH_ACCOUNT_MS;
  const noFollowers = profile.followers === 0;
  const noStars = profile.ownedRepoStars === 0;
  const onlyPrs =
    profile.otherPullRequests !== null &&
    profile.mergedPullRequests !== null &&
    profile.otherPullRequests > 0 &&
    profile.mergedPullRequests === 0;
  const signals: ExternalScrutinySignal[] = [
    ...(fresh ? (['fresh_account'] as const) : []),
    ...(noFollowers ? (['no_followers'] as const) : []),
    ...(noStars ? (['no_starred_repos'] as const) : []),
    ...(onlyPrs ? (['only_pull_requests'] as const) : []),
  ];
  return { level: noFollowers && noStars && (fresh || onlyPrs) ? 'raised' : 'none', signals };
}

/** The next thing the core does with one text. */
export type ExternalDangerStep =
  | { readonly kind: 'clear' }
  | { readonly kind: 'read_profile' }
  | { readonly kind: 'second_read' }
  | { readonly kind: 'hold'; readonly category: ExternalDangerCategory }
  /** One dangerous read the other did not back: open, audited, and the ordinary path's. */
  | { readonly kind: 'disagree'; readonly category: ExternalDangerCategory }
  /** Two dangerous reads: closed and banned, one act. */
  | { readonly kind: 'ban'; readonly category: ExternalDangerCategory };

/** The ladder; see the module header. Total over what has been learned so far. */
export function externalDangerStep(known: {
  readonly first: ExternalDangerVerdict;
  readonly scrutiny?: ExternalScrutiny;
  /** `unavailable`: the second read was asked and could not answer. */
  readonly second?: ExternalDangerVerdict | 'unavailable';
}): ExternalDangerStep {
  const { first, scrutiny, second } = known;
  if (first.verdict === 'clean') return { kind: 'clear' };
  const secondDangerous =
    second !== undefined && second !== 'unavailable' && second.verdict === 'dangerous';
  if (first.verdict === 'dangerous') {
    if (second === undefined) return { kind: 'second_read' };
    return { kind: secondDangerous ? 'ban' : 'disagree', category: first.category };
  }
  if (scrutiny === undefined) return { kind: 'read_profile' };
  if (scrutiny.level === 'none') return { kind: 'hold', category: first.category };
  if (second === undefined) return { kind: 'second_read' };
  return secondDangerous
    ? { kind: 'disagree', category: second.category }
    : { kind: 'hold', category: first.category };
}

/**
 * Distinct items one outside author may have judged on one account in a day. THE FAILURE IT NAMES
 * (`docs/standards/agent-budgets.md`): a stranger with no relationship to the customer drives model
 * calls on the customer's BYOK key, and past the key's rate limit the key cools, which stops the
 * customer's own reviews and fleet. It is not a bill: it binds nobody the repo answers `developer`,
 * and nothing an agent does. A push to an item already counted is not a new item, and a read of a
 * text already judged is not counted at all.
 *
 * AN ITEM OVER ANY LIMIT WAITS. It never closes: ten sock accounts must not be able to close every
 * outsider's issue on a repo by spending its limit.
 *
 * The number: prod saw 0 external PRs of 5,157 in the 30 days to 2026-10-31 and 1 stamped outside
 * ask of 2,504 in 90 days, so five new items a day is far past a real contributor. RETUNE on the
 * first `external_ban.enforced` row with `rule: over_author_limit` a maintainer then asks about.
 */
export const EXTERNAL_AUTHOR_ITEMS_PER_DAY = 5;

/**
 * Distinct items all outside authors together may have judged on one repo in a day: many accounts
 * each under {@link EXTERNAL_AUTHOR_ITEMS_PER_DAY} are the same failure. Ten of them at their limit.
 */
export const EXTERNAL_REPO_ITEMS_PER_DAY = 50;

/**
 * Judged reads one item may cost in a day. THE FAILURE: counting items lets one PR pushed in a loop
 * spend the same key without bound. Ten pushes of two reads each, far past an honest day's work.
 */
export const EXTERNAL_ITEM_READS_PER_DAY = 20;

const LIMIT_WINDOW_MS = 86_400_000;

/** The start of the window the limits count over. */
export function externalReadsWindowStart(now: Date): Date {
  return new Date(now.getTime() - LIMIT_WINDOW_MS);
}

export type ExternalLimitVerdict =
  | { readonly kind: 'under' }
  | {
      readonly kind: 'over';
      readonly scope: 'author' | 'repo' | 'item';
      readonly reads: number;
      readonly limit: number;
    };

/**
 * May one more text be judged? `authorItems` and `repoItems` count OTHER items than this one, so an
 * item already in the count never trips them. The author reads first: it names the one who spent it.
 */
export function externalLimitVerdict(counts: {
  readonly authorItems: number;
  readonly repoItems: number;
  readonly itemReads: number;
}): ExternalLimitVerdict {
  const over = (scope: 'author' | 'repo' | 'item', reads: number, limit: number) =>
    ({ kind: 'over', scope, reads, limit }) as const;
  if (counts.authorItems >= EXTERNAL_AUTHOR_ITEMS_PER_DAY) {
    return over('author', counts.authorItems, EXTERNAL_AUTHOR_ITEMS_PER_DAY);
  }
  if (counts.repoItems >= EXTERNAL_REPO_ITEMS_PER_DAY) {
    return over('repo', counts.repoItems, EXTERNAL_REPO_ITEMS_PER_DAY);
  }
  if (counts.itemReads >= EXTERNAL_ITEM_READS_PER_DAY) {
    return over('item', counts.itemReads, EXTERNAL_ITEM_READS_PER_DAY);
  }
  return { kind: 'under' };
}

/** Why one account's ban does not count toward the shared list. Named, never a `false`. */
export type CorroborationRefusal =
  | 'private_repo'
  | 'custom_endpoint'
  | 'not_paying'
  | 'account_too_young';

/**
 * How old an account must be to witness. THE FAILURE: disjoint memberships alone let one person buy
 * three Solo accounts in an afternoon and ban anyone platform-wide. A month makes that a month's plan
 * on three subscriptions, and a staff entry has no such wait.
 */
export const EXTERNAL_BAN_WITNESS_MIN_AGE_MS = 30 * 86_400_000;

const witnessAged = (accountCreatedAt: Date, now: Date): boolean =>
  now.getTime() - accountCreatedAt.getTime() >= EXTERNAL_BAN_WITNESS_MIN_AGE_MS;

const PAYING_TIERS: ReadonlySet<Tier> = new Set<Tier>([
  Tiers.Solo,
  Tiers.Pro,
  Tiers.OssDonor,
  Tiers.Enterprise,
]);

/**
 * A key whose answers may count across accounts. THE FAILURE: a tenant's own endpoint answering
 * `dangerous` to everything would ban anyone platform-wide. So `custom` never counts, and neither
 * does a known provider's key whose `baseUrl` names a host the platform does not recognize, since
 * a key's `baseUrl` wins over its provider (`wireFor`) and the label alone proves nothing.
 */
function keyIsKnownProvider(provider: string, baseUrl: string | null): boolean {
  const parsed = BYOKProviderSchema.safeParse(provider);
  if (!parsed.success || parsed.data === BYOKProvider.Custom) return false;
  return baseUrl === null || endpointWire(baseUrl) !== null;
}

/**
 * Does this account's confirmed verdict count toward the shared list? Only off a public repo: the
 * row it writes is the only thing that crosses accounts, and nothing from a private repo may.
 */
export function corroborationQualifies(input: {
  readonly repoPrivate: boolean;
  readonly provider: string;
  readonly baseUrl: string | null;
  readonly tier: Tier;
  readonly accountCreatedAt: Date;
  readonly now: Date;
}): { readonly ok: true } | { readonly ok: false; readonly why: CorroborationRefusal } {
  if (input.repoPrivate) return { ok: false, why: 'private_repo' };
  if (!keyIsKnownProvider(input.provider, input.baseUrl)) {
    return { ok: false, why: 'custom_endpoint' };
  }
  if (!PAYING_TIERS.has(input.tier)) return { ok: false, why: 'not_paying' };
  if (!witnessAged(input.accountCreatedAt, input.now))
    return { ok: false, why: 'account_too_young' };
  return { ok: true };
}

/** Unrelated accounts a shared-list promotion takes (#7785, owner ruling). */
export const EXTERNAL_BAN_PROMOTION_ACCOUNTS = 3;

/** One account that counted, and the people on it. */
export type BanCorroborator = {
  readonly accountId: string;
  readonly memberUserIds: readonly string[];
  readonly tier: Tier;
  readonly accountCreatedAt: Date;
};

/** The largest pairwise-disjoint set up to `want`, searched rather than greedy. */
function disjointCount(
  rest: readonly BanCorroborator[],
  taken: ReadonlySet<string>,
  want: number,
): number {
  if (want === 0) return 0;
  let best = 0;
  for (const [i, c] of rest.entries()) {
    if (c.memberUserIds.some((u) => taken.has(u))) continue;
    const under = new Set([...taken, ...c.memberUserIds]);
    best = Math.max(best, 1 + disjointCount(rest.slice(i + 1), under, want - 1));
    if (best === want) return best;
  }
  return best;
}

/**
 * Do these accounts make {@link EXTERNAL_BAN_PROMOTION_ACCOUNTS} unrelated witnesses? Accounts that
 * share any member are one witness, an account with no members is none, and an account that stopped
 * paying or is too young is none, read again here because a witness row outlives what earned it.
 */
export function promotionVerdict(
  corroborators: readonly BanCorroborator[],
  now: Date,
): {
  readonly promote: boolean;
  readonly unrelated: number;
} {
  const witnesses = corroborators.filter(
    (c) =>
      c.memberUserIds.length > 0 &&
      PAYING_TIERS.has(c.tier) &&
      witnessAged(c.accountCreatedAt, now),
  );
  const unrelated = disjointCount(witnesses, new Set(), EXTERNAL_BAN_PROMOTION_ACCOUNTS);
  return { promote: unrelated >= EXTERNAL_BAN_PROMOTION_ACCOUNTS, unrelated };
}

/**
 * Who cannot be banned. A bot never, by verdict or by a door: no bot-on-bot. GitHub's own `type`
 * decides as well as the login. `null` is an id nobody could name, which proves it is not a bot.
 */
export function externalBanTargetRefusal(target: {
  readonly login: string | null;
  readonly type?: string | undefined;
}): 'bot_author' | 'unknown_github_user' | null {
  if (target.login === null) return 'unknown_github_user';
  return looksLikeBot({ login: target.login, type: target.type }) ? 'bot_author' : null;
}

/**
 * What a maintainer sees of a verdict ban: where to look and what read it. No author text: the
 * item itself stays on GitHub, closed and never deleted. Stored on the account's row only, so it
 * never crosses accounts.
 */
export const ExternalBanEvidenceSchema = z.object({
  repo: z.string().min(1),
  itemKind: ExternalItemKindSchema,
  itemNumber: z.number().int().positive(),
  headSha: z.string().nullable(),
  textSha256: z.string().min(1),
  promptVersion: z.string().min(1),
  model: z.string().nullable(),
  signals: z.array(ExternalScrutinySignalSchema),
});
export type ExternalBanEvidence = z.infer<typeof ExternalBanEvidenceSchema>;

/** One account's ban. `githubUserId` is the identity; `login` a snapshot for reading. */
export type ExternalBan = {
  readonly accountId: string;
  readonly githubUserId: number;
  readonly login: string | null;
  readonly source: ExternalBanSource;
  readonly category: ExternalDangerCategory | null;
  readonly evidence: ExternalBanEvidence | null;
  readonly addedBy: string | null;
  readonly createdAt: Date;
};

/** One shared-list entry: an id and a mechanism, nothing of any account. */
export type ExternalSharedBan = {
  readonly githubUserId: number;
  readonly login: string | null;
  readonly source: ExternalSharedBanSource;
  readonly category: ExternalDangerCategory | null;
  readonly addedBy: string | null;
  readonly createdAt: Date;
};

/** Which list holds an author, as an enforcement reads it. */
export type ActiveExternalBan =
  | { readonly list: 'account'; readonly ban: ExternalBan }
  | { readonly list: 'shared'; readonly ban: ExternalSharedBan };

/** One merged PR queued for a second look after its author's ban. */
export type ExternalBanRereview = {
  readonly accountId: string;
  readonly repoId: string;
  readonly repoFullName: string;
  readonly prNumber: number;
  readonly githubUserId: number;
  readonly filedRawTaskId: string | null;
  readonly createdAt: Date;
};

/** A door's refusal. `detail` is rendered per locale at the door. */
export type ExternalBanRefusal =
  | { readonly kind: 'no_human_actor' }
  | { readonly kind: 'not_platform_staff' }
  | { readonly kind: 'bot_author' }
  | { readonly kind: 'unknown_github_user' }
  | { readonly kind: 'repo_developer' }
  | { readonly kind: 'standing_unread' }
  | { readonly kind: 'not_banned' }
  | { readonly kind: 'on_shared_list' };
